// Estrutura do menu lateral: 4 itens-pai recolhíveis. `pronto: true` marca as
// telas já construídas — só elas aparecem no menu e respondem ao roteamento.
// As demais ficam registradas aqui como mapa do que falta.
//
// Permissão de Módulo (issue #71): um grupo do menu (ou uma tela solta do ⚙)
// pode declarar `modulo` — sem ao menos leitura nesse Módulo, some do menu e
// do roteamento. É só esconder na UI (decisão do spec): o servidor continua
// sendo quem de fato recusa um Comando fora do Módulo permitido.

import type { Modulo } from "../domain/schema.ts";
import type { NomeIcone } from "./icons.ts";

export interface NavLeaf {
  readonly id: string;
  readonly label: string;
  /** A tela existe? Só as prontas aparecem no menu. */
  readonly pronto?: boolean;
  readonly icone?: NomeIcone;
  /** Módulo dono da tela — só usado nas telas soltas do ⚙ (`TELAS_ENGRENAGEM`). */
  readonly modulo?: Modulo;
}

export interface NavGroup {
  readonly id: string;
  readonly label: string;
  readonly pronto?: boolean;
  readonly icone?: NomeIcone;
  /** Módulo dono do grupo — vale para todos os filhos. `undefined` = sempre visível. */
  readonly modulo?: Modulo;
  readonly children: readonly NavLeaf[];
}

/** Diz se o Perfil do usuário logado tem ao menos leitura num Módulo. */
export type PermiteModulo = (modulo: Modulo) => boolean;

const SEMPRE_PERMITE: PermiteModulo = () => true;

export const NAV: readonly NavGroup[] = [
  {
    id: "operacional",
    label: "Operacional",
    icone: "operacional",
    modulo: "operacional",
    children: [
      // a tela inicial de quem tem o Operacional (ADR-0032); rota continua #/dashboard
      { id: "dashboard", label: "Dashboard Operacional", pronto: true, icone: "dashboard" },
      { id: "lancar-os", label: "Lançar OS", pronto: true, icone: "lancar-os" },
      { id: "backlog", label: "Backlog", pronto: true, icone: "backlog" },
      {
        id: "cronograma-coleta-devolucao",
        label: "Cronograma de Coleta/Devolução",
        pronto: true,
        icone: "cronograma",
      },
      { id: "kanban", label: "Acompanhamento de Etapas" },
      { id: "conferencia", label: "Conferência" },
      { id: "fluxo-pecas", label: "Fluxo de Peças" },
      { id: "estoque", label: "Estoque" },
      { id: "movimentacao-estoque", label: "Movimentação de Estoque" },
    ],
  },
  {
    id: "financeiro",
    label: "Financeiro",
    icone: "financeiro",
    modulo: "financeiro",
    children: [
      { id: "faturamento", label: "Faturamento por Cliente", pronto: true, icone: "faturamento" },
      { id: "retiradas-pessoais", label: "Retiradas Pessoais", pronto: true, icone: "retiradas" },
      { id: "contas-pagar", label: "Contas a Pagar", pronto: true, icone: "contas-pagar" },
      { id: "contas-receber", label: "Contas a Receber", pronto: true, icone: "contas-receber" },
      { id: "dashboard-financeiro", label: "Dashboard Financeiro" },
    ],
  },
  {
    id: "configuracao",
    label: "Cadastros",
    icone: "cadastros",
    modulo: "configuracao",
    children: [
      { id: "clientes", label: "Clientes", pronto: true, icone: "clientes" },
      { id: "tabela-precos", label: "Tabela de Preços", pronto: true, icone: "tabela-precos" },
      { id: "tipos-peca", label: "Tipos de Peça", pronto: true, icone: "tipos-peca" },
      { id: "funcionarios", label: "Funcionários", pronto: true, icone: "funcionarios" },
      { id: "insumos", label: "Produtos/Insumos", pronto: true, icone: "insumos" },
    ],
  },
];

/**
 * Telas de administração, fora do menu lateral — ficam no menu da engrenagem
 * (⚙) no topo. Continuam roteáveis por hash.
 */
export const TELAS_ENGRENAGEM: readonly NavLeaf[] = [
  { id: "usuarios", label: "Usuários", pronto: true, icone: "usuarios", modulo: "configuracao" },
  { id: "perfis", label: "Perfis", pronto: true, icone: "permissoes", modulo: "configuracao" },
  {
    id: "parametros",
    label: "Parâmetros do Sistema",
    pronto: true,
    icone: "parametros",
    modulo: "configuracao",
  },
  { id: "auditoria", label: "Auditoria", pronto: true, icone: "auditoria", modulo: "configuracao" },
];

/**
 * O menu como ele aparece: só telas prontas, e só Módulos que o Perfil do
 * usuário logado pode ao menos ler (`permite`). Um pai sem nenhum filho
 * pronto some junto. Sem `permite` (modo local, sem Usuário logado), tudo
 * aparece — é o comportamento de sempre.
 */
export function navVisivel(permite: PermiteModulo = SEMPRE_PERMITE): NavGroup[] {
  const visiveis: NavGroup[] = [];
  for (const g of NAV) {
    if (g.modulo != null && !permite(g.modulo)) continue;
    if (g.children.length === 0) {
      if (g.pronto) visiveis.push(g);
      continue;
    }
    const children = g.children.filter((c) => c.pronto);
    if (children.length > 0) visiveis.push({ ...g, children });
  }
  return visiveis;
}

/** As telas soltas do ⚙ que o Perfil do usuário logado pode ver. */
export function telasEngrenagemVisiveis(
  permite: PermiteModulo = SEMPRE_PERMITE,
): readonly NavLeaf[] {
  return TELAS_ENGRENAGEM.filter(
    (t) => t.pronto && (t.modulo == null || permite(t.modulo)),
  );
}

/** Ids das telas que existem, para roteamento. Uma rota fora daqui cai na `telaInicial`. */
export function allScreenIds(permite: PermiteModulo = SEMPRE_PERMITE): string[] {
  const ids: string[] = [];
  for (const g of navVisivel(permite)) {
    if (g.children.length === 0) ids.push(g.id);
    for (const c of g.children) ids.push(c.id);
  }
  for (const t of telasEngrenagemVisiveis(permite)) ids.push(t.id);
  return ids;
}

/** Rótulo de uma tela pelo id (pronta ou não), ou o próprio id se não achar. */
export function screenLabel(id: string): string {
  for (const g of NAV) {
    if (g.id === id) return g.label;
    for (const c of g.children) if (c.id === id) return c.label;
  }
  for (const t of TELAS_ENGRENAGEM) if (t.id === id) return t.label;
  return id;
}

/**
 * Onde o app abre (e para onde cai uma rota que não existe): o Dashboard
 * Operacional, ou — sem acesso ao Operacional — a primeira tela que o Perfil
 * pode ver. Sem nenhuma, "dashboard" (a casca mostra o aviso de tela vazia).
 */
export function telaInicial(permite: PermiteModulo = SEMPRE_PERMITE): string {
  const ids = allScreenIds(permite);
  return ids.includes("dashboard") ? "dashboard" : (ids[0] ?? "dashboard");
}